import { useState, useEffect, useCallback, useRef } from "react";

/* グループキーを作成（同一商品判定用） */
const makeGroupKey = (p) => [
    p.productId,
    // p.commodityOwnerCd,
    p.originId ?? "",
    p.rankId ?? "",
    p.productClassId ?? "",
    p.packingId ?? "",
    p.weight ?? "",
    p.weightUnitId ?? "",
].join("-");

/* 配列 → グループ配列 [{ key, items, rep }] へ変換 */
const groupProducts = (list = []) => {
    const map = new Map();
    list.forEach((p) => {
        const key = makeGroupKey(p);
        if (!map.has(key)) map.set(key, []);
        map.get(key).push(p);
    });
    return Array.from(map.entries()).map(([key, items]) => ({
        key,
        items,
        rep: { ...items[0], _groupKey: key }, // 代表オブジェクト
    }));
};

export default function useProductHistoryModal() {
    /* ────────── 1. モーダル制御 ────────── */
    const [open, setOpen] = useState(false);
    const [formDirty, setFormDirty] = useState(false);
    const openModal  = () => {setOpen(true); setFormDirty(true);}
    const closeModal = () => setOpen(false);
    const resetFormDirty = () => setFormDirty(false);

    /* ────────── 1.5. サイドバー制御 ────────── */
    const [sidebarOpen, setSidebarOpen] = useState(false);
    const openSidebar = () => { setSidebarOpen(true); setFormDirty(true); };
    const closeSidebar = () => setSidebarOpen(false);
    const toggleSidebar = () => { setSidebarOpen(prev => !prev); setFormDirty(true); };

    /* ────────── 2. データ（グループ単位） ────────── */
    const [displayedGrp, setDisplayedGrp] = useState([]); // isHidden = false
    const [hiddenGrp,    setHiddenGrp]    = useState([]); // isHidden = true
    const [showHidden, setShowHidden] = useState(false);

    /* チェック状態：key の配列で持つ */
    const [selected, setSelected] = useState({ displayed: [], hidden: [] });

    /* データ変更時のコールバック（refで保持して後から設定可能にする） */
    const onDataChangeRef = useRef(null);
    const setOnDataChange = useCallback((callback) => {
        onDataChangeRef.current = callback;
    }, []);

    /* データ変更時のコールバック呼び出し */
    const notifyDataChange = useCallback(() => {
        if (onDataChangeRef.current) {
            onDataChangeRef.current();
        }
    }, []);

    /* ------ 共通ユーティリティ ------ */
    const toRepArray = (grpArr) => grpArr.map((g) => g.rep);

    const splitAndGroup = (all = []) => {
        const disp = groupProducts(all.filter(p => !p.isHidden));
        const hid  = groupProducts(all.filter(p =>  p.isHidden));
        setDisplayedGrp(disp);
        setHiddenGrp(hid);
        setSelected({ displayed: [], hidden: [] });
    };

    /* ========= A. 外部からデータを流し込む ========= */
    const setHistoryData = useCallback((list) => {
        splitAndGroup(list);
    }, []);

    /* ========= B. 必要なら内部で取得 ========= */
    const fetchHistory = useCallback(async () => {
        // const { data } = await postSearchSupplyHistory();
        // splitAndGroup(data ?? []);
    }, []);

    /* モーダルを開いた時にローカルにデータが無ければ取得 */
    useEffect(() => {
        if (open && displayedGrp.length === 0 && hiddenGrp.length === 0) {
            fetchHistory();
        }
    }, [open, displayedGrp.length, hiddenGrp.length, fetchHistory]);

    /* ────────── 3. 単品ハンドラ（グループ単位） ────────── */
    const moveGroup = (from, to, key, makeHiddenFlag) => {
        /* from/to は setState 関数, makeHiddenFlag = true|false */
        setDisplayedGrp((prev) =>
            from === setDisplayedGrp
                ? prev.filter((g) => g.key !== key)
                : prev
        );
        setHiddenGrp((prev) =>
            from === setHiddenGrp
                ? prev.filter((g) => g.key !== key)
                : prev
        );

        /* 移動先へ追加 */
        to((prev) => [
            ...prev,
            {
                ...from === setDisplayedGrp
                    ? displayedGrp.find((g) => g.key === key)
                    : hiddenGrp.find((g) => g.key === key),
                rep: { ...((from === setDisplayedGrp
                        ? displayedGrp
                        : hiddenGrp).find((g) => g.key === key).rep),
                    isHidden: makeHiddenFlag },
                items: ((from === setDisplayedGrp
                    ? displayedGrp
                    : hiddenGrp).find((g) => g.key === key).items)
                    .map((p) => ({ ...p, isHidden: makeHiddenFlag })),
                key,
            },
        ]);
    };

    const handleHide = (repProduct) => {
        const key   = repProduct._groupKey ?? makeGroupKey(repProduct);
        const group = displayedGrp.find((g) => g.key === key);

        if (group) {
            /* 表示 → 非表示 へ移動 */
            moveGroup(setDisplayedGrp, setHiddenGrp, key, true);
        } else {
            /* displayedGrp にない場合（メインテーブルから直接非表示） → hiddenGrp に新規追加 */
            const hiddenProduct = { ...repProduct, isHidden: true, _groupKey: key };
            setHiddenGrp((prev) => [
                ...prev,
                { key, items: [hiddenProduct], rep: hiddenProduct },
            ]);
        }

        setSelected((prev) => ({
            ...prev,
            displayed: prev.displayed.filter((k) => k !== key),
        }));
    };

    const handleBack = (repProduct) => {
        const key   = repProduct._groupKey ?? makeGroupKey(repProduct);
        const group = hiddenGrp.find((g) => g.key === key);
        if (!group) return;

        /* 非表示 → 表示 へ移動 (Frontend only - no API call) */
        moveGroup(setHiddenGrp, setDisplayedGrp, key, false);

        setSelected((prev) => ({
            ...prev,
            hidden: prev.hidden.filter((k) => k !== key),
        }));
    };

    /* ────────── 4. 一括ハンドラ (Frontend only - no API call) ────────── */
    const handleBulkHide = () => {
        if (selected.displayed.length === 0) return;

        /* ローカル更新のみ */
        const moved = displayedGrp.filter((g) => selected.displayed.includes(g.key))
            .map((g) => ({
                ...g,
                rep: { ...g.rep, isHidden: true },
                items: g.items.map((i) => ({ ...i, isHidden: true })),
            }));
        setDisplayedGrp((prev) => prev.filter((g) => !selected.displayed.includes(g.key)));
        setHiddenGrp((prev) => [...prev, ...moved]);
        setSelected((prev) => ({ ...prev, displayed: [] }));
    };

    const handleBulkBack = () => {
        if (selected.hidden.length === 0) return;

        /* ローカル更新のみ */
        const moved = hiddenGrp.filter((g) => selected.hidden.includes(g.key))
            .map((g) => ({
                ...g,
                rep: { ...g.rep, isHidden: false },
                items: g.items.map((i) => ({ ...i, isHidden: false })),
            }));
        setHiddenGrp((prev) => prev.filter((g) => !selected.hidden.includes(g.key)));
        setDisplayedGrp((prev) => [...prev, ...moved]);
        setSelected((prev) => ({ ...prev, hidden: [] }));
    };

    /* ────────── 5. チェック操作 ────────── */
    const toggleSelect = (repProduct, isHiddenList) => {
        const key = repProduct._groupKey ?? makeGroupKey(repProduct);
        const listKey = isHiddenList ? "hidden" : "displayed";
        setSelected((prev) => {
            const exists = prev[listKey].includes(key);
            return {
                ...prev,
                [listKey]: exists
                    ? prev[listKey].filter((k) => k !== key)
                    : [...prev[listKey], key],
            };
        });
    };

    const handleToggleAllDisplayed = () => {
        setSelected((prev) => ({
            ...prev,
            displayed:
                prev.displayed.length === displayedGrp.length
                    ? []
                    : displayedGrp.map((g) => g.key),
        }));
    };

    const handleToggleAllHidden = () => {
        setSelected((prev) => ({
            ...prev,
            hidden:
                prev.hidden.length === hiddenGrp.length
                    ? []
                    : hiddenGrp.map((g) => g.key),
        }));
    };

    /* ────────── 5.5. ソート操作（ドラッグ&ドロップ） ────────── */
    const moveHiddenItem = useCallback((dragIndex, hoverIndex) => {
        setHiddenGrp((prev) => {
            const newList = [...prev];
            const [draggedItem] = newList.splice(dragIndex, 1);
            newList.splice(hoverIndex, 0, draggedItem);
            return newList;
        });
    }, []);

    const moveDisplayedItem = useCallback((dragIndex, hoverIndex) => {
        setDisplayedGrp((prev) => {
            const newList = [...prev];
            const [draggedItem] = newList.splice(dragIndex, 1);
            newList.splice(hoverIndex, 0, draggedItem);
            return newList;
        });
    }, []);

    /* ────────── 6. 返却値（UI へ） ────────── */
    return {
        /* モーダル */
        open, openModal, closeModal, formDirty, resetFormDirty,
        /* サイドバー */
        sidebarOpen, openSidebar, closeSidebar, toggleSidebar,
        /* 代表オブジェクト配列を UI に渡す */
        displayed : toRepArray(displayedGrp),
        hidden    : toRepArray(hiddenGrp),
        showHidden, setShowHidden,

        /* 選択状態（key 配列） */
        selected,

        /* ハンドラ */
        handleHide, handleBack,
        handleBulkHide, handleBulkBack,
        toggleSelect,
        handleToggleAllDisplayed,
        handleToggleAllHidden,

        /* ソート操作 */
        moveHiddenItem,
        moveDisplayedItem,

        /* 外部 setter */
        setHistoryData,
        setOnDataChange,
    };
}
